import { ChangeEvent, useEffect, useState } from "react";
import { Field, Label, Switch } from "@headlessui/react";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import KeyValuePair from "../../interfaces/keyValuePair";
import MultiSelect from "../peerReviewGroups/MultiSelect";
import {
	EmailPromptViewModel,
	PromptEnabledInCoursesViewModel,
	SavePromptViewModel,
} from "./promptsApiSlice";

interface SharePromptStepProps {
	model: SavePromptViewModel;
	emailListModel?: EmailPromptViewModel;
	promptEnabledInCoursesList: PromptEnabledInCoursesViewModel[];
	setModel: (model: SavePromptViewModel) => void;
	setEmailListModel: (model: EmailPromptViewModel) => void;
}

export default function SharePromptStep(props: SharePromptStepProps) {
	const { model, promptEnabledInCoursesList, emailListModel, setModel, setEmailListModel } =
		props;

	const availableTeachersPool =
		emailListModel?.proctors.map((x) => ({
			key: Number(x.value),
			value: x.text,
		})) ?? [];

	const initializeTeachers = () => {
		const selected =
			emailListModel?.proctors
				.filter((p) => emailListModel.proctorIds?.includes(Number(p.value)))
				.map((p) => ({ key: Number(p.value), value: p.text })) ?? [];
		return selected;
	};

	const [shareEnabled, setShareEnabled] = useState(model.isShared);
	const [selectedCourses, setSelectedCourses] = useState<string[]>(
		model.selectedPromptEnabledInCoursesList ?? []
	);
	const [selectedTeachers, setSelectedTeachers] = useState(initializeTeachers());

	function handleCheckboxChange(e: ChangeEvent<HTMLInputElement>) {
		if (e.target.checked) {
			setSelectedCourses([...selectedCourses, e.target.value]);
		} else {
			setSelectedCourses(selectedCourses.filter((item) => item !== e.target.value));
		}
	}

	function handleSelectedTeachersChange(selected: KeyValuePair<number, string>[]) {
		setSelectedTeachers(selected);
	}

	useEffect(() => {
		setModel(updateModel());
	}, [shareEnabled, selectedCourses]);

	useEffect(() => {
		var model = updateEmailModel();
		if (model) {
			setEmailListModel(model);
		}
	}, [selectedTeachers]);

	function updateModel(): SavePromptViewModel {
		const newModel: SavePromptViewModel = {
			...model,
			isShared: shareEnabled,
			selectedPromptEnabledInCoursesList: selectedCourses,
		};
		return newModel;
	}

	function updateEmailModel(): EmailPromptViewModel | undefined {
		if (emailListModel) {
			const emailModel: EmailPromptViewModel = {
				...emailListModel,
				proctorIds: selectedTeachers.map((u) => u.key),
			};
			return emailModel;
		}
	}

	return (
		<section className="space-y-6">
			<div
				className={`${model.canEdit || model.isNew ? "border-b border-gray-900/10" : ""} flex pb-4`}>
				<div className="w-full md:w-1/4 pr-3">
					<h2 className="leading-7">Courses</h2>
					<p className="mt-1 mr-6 text-sm leading-6 text-gray-600">
						To add or remove this prompt from a course, check or uncheck the
						corresponding box.
					</p>
				</div>
				<Field className="flex flex-col w-full md:w-3/4">
					<Label as="span" className="block font-medium leading-6">
						Courses
					</Label>
					<table className="tbl tbl-data mt-2">
						<tbody>
							{promptEnabledInCoursesList.map((course) => {
								return (
									<tr key={"course-" + course.value}>
										<td>
											<input
												value={course.value}
												checked={selectedCourses.includes(course.value)}
												type="checkbox"
												className="size-4 rounded-sm border-gray-300 text-primary-700 focus:ring-primary-700"
												onChange={(e) => {
													handleCheckboxChange(e);
												}}
											/>
										</td>
										<td>
											{course.text}{" "}
											{course.isCreatedInCourse && (
												<span className="text-sm pl-1.5 text-gray-600">
													(created in course)
												</span>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</Field>
			</div>
			{(model.isNew || model.canEdit) && (
				<div className="flex pb-4">
					<div className="w-full md:w-1/4 pr-3">
						<h2 className="leading-7">Share Prompt</h2>
						<p className="mt-1 mr-6 text-sm leading-6 text-gray-600">
							{model.wasAlreadyShared
								? "This prompt has been shared with all other teachers in your school who teach the same grade level as the course the prompt was created in. It appears in the prompt list as a School Prompt. Please remember, once you share a prompt, it cannot be unshared."
								: "This will share the prompt with all other teachers in your school who teach the same grade level as the course the prompt was created in. It will appear as a School Prompt in the prompt list. Once shared, a prompt cannot be unshared."}
						</p>
					</div>
					<div className="flex flex-col w-full gap-y-4 md:w-3/4">
						<Field className="flex items-center justify-between">
							<Label as="span" className="block font-medium leading-6">
								Share to School
							</Label>
							<Switch
								checked={shareEnabled}
								onChange={setShareEnabled}
								disabled={model.wasAlreadyShared}
								className="group relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600 disabled:cursor-not-allowed">
								{model.wasAlreadyShared ? (
									<span
										aria-hidden="true"
										className="pointer-events-none absolute inset-0 flex items-center justify-center size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5">
										<LockClosedIcon className="size-5 items-center text-gray-500" />
									</span>
								) : (
									<span
										aria-hidden="true"
										className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
									/>
								)}
							</Switch>
						</Field>
						{!model.wasAlreadyShared &&
							shareEnabled &&
							emailListModel &&
							availableTeachersPool.length > 0 && (
								<Field className="flex flex-col w-full">
									<Label as="span" className="block font-medium leading-6">
										Notify Teachers
									</Label>
									<MultiSelect
										pool={availableTeachersPool}
										selected={selectedTeachers}
										className="w-full mt-2"
										defaultText="Choose a teacher..."
										onSelectedChange={handleSelectedTeachersChange}
									/>
								</Field>
							)}
					</div>
				</div>
			)}
		</section>
	);
}
